/**
 * @file routes/_app/analytics/auditors.tsx
 * @description 稽核員排行頁。
 *
 * 顯示每位稽核員的案件數、平均處理天數、SLA 達成率。
 * 支援日期範圍篩選 + Excel 匯出。
 *
 * 視覺：PageShell + SectionCard + EmptyState（recharts 橫向 BarChart + antd Table）
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { Button, DatePicker, Space, Spin, Table, Progress } from 'antd';
import { message } from '@/lib/antd-app';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { FileDown, RefreshCw, Award } from 'lucide-react';
import { type Dayjs } from 'dayjs';
import { useAuditorStats } from '@/hooks/useAnalytics';
import type { AuditorStatItem } from '@/lib/analytics-types';
import { PageShell, SectionCard, EmptyState, StatusPill } from '@/components/shell';
import { color, space } from '@/styles/tokens';

const { RangePicker } = DatePicker;

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/analytics/auditors')({
  component: AnalyticsAuditorsPage,
});

/* ── SLA 達成率顏色 ── */
function slaColor(rate: number): string {
  if (rate >= 90) return color.state.success;
  if (rate >= 70) return color.state.warning;
  return color.state.danger;
}

/* ── SLA 標籤 tone ── */
function slaTone(rate: number): 'success' | 'warning' | 'danger' {
  if (rate >= 90) return 'success';
  if (rate >= 70) return 'warning';
  return 'danger';
}

/* ── SLA 標籤文字 ── */
function slaLabel(rate: number): string {
  if (rate >= 90) return '優';
  if (rate >= 70) return '良';
  return '待改善';
}

/** 稽核員排行頁 */
function AnalyticsAuditorsPage() {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [exporting, setExporting] = useState(false);

  const range = {
    from: dateRange?.[0]?.format('YYYY-MM-DD'),
    to: dateRange?.[1]?.format('YYYY-MM-DD'),
  };

  const { data, isLoading, isFetching, refetch } = useAuditorStats(range);
  const auditors = data?.auditors ?? [];

  /* ── 匯出 ── */
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ type: 'auditors' });
      if (range.from) params.set('from', range.from);
      if (range.to) params.set('to', range.to);
      const resp = await fetch(`/api/analytics/export?${params.toString()}`, {
        credentials: 'same-origin',
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zhcb-auditors-analytics-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('匯出成功');
    } catch (err) {
      message.error('匯出失敗');
      console.error('export auditors analytics failed', err);
    } finally {
      setExporting(false);
    }
  }, [range]);

  /* ── 表格欄位 ── */
  const columns = [
    {
      title: '排名',
      key: 'rank',
      width: 70,
      render: (_: unknown, __: AuditorStatItem, idx: number) => (
        <span
          style={{
            fontWeight: idx < 3 ? 700 : 400,
            color: idx < 3 ? color.brand.primary : undefined,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {idx < 3 && <Award size={12} />}
          {idx + 1}
        </span>
      ),
    },
    {
      title: '稽核員',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '案件數',
      dataIndex: 'caseCount',
      key: 'caseCount',
      width: 100,
      sorter: (a: AuditorStatItem, b: AuditorStatItem) => a.caseCount - b.caseCount,
      render: (v: number) => `${v} 件`,
    },
    {
      title: '平均處理天數',
      dataIndex: 'avgProcessingDays',
      key: 'avgProcessingDays',
      width: 140,
      sorter: (a: AuditorStatItem, b: AuditorStatItem) => a.avgProcessingDays - b.avgProcessingDays,
      render: (v: number) => `${v} 天`,
    },
    {
      title: 'SLA 達成率',
      dataIndex: 'slaHitRate',
      key: 'slaHitRate',
      width: 240,
      sorter: (a: AuditorStatItem, b: AuditorStatItem) => a.slaHitRate - b.slaHitRate,
      render: (v: number) => (
        <Space>
          <Progress
            percent={v}
            size="small"
            strokeColor={slaColor(v)}
            style={{ width: 100 }}
            format={(p) => `${p}%`}
          />
          <StatusPill tone={slaTone(v)} size="sm">
            {slaLabel(v)}
          </StatusPill>
        </Space>
      ),
    },
  ];

  return (
    <PageShell
      title="稽核員排行"
      description="各稽核員案件量、處理效率與 SLA 達成率"
      actions={
        <Space>
          <Button
            icon={<RefreshCw size={14} />}
            onClick={() => void refetch()}
            loading={isFetching}
          >
            刷新
          </Button>
          <Button icon={<FileDown size={14} />} loading={exporting} onClick={handleExport}>
            匯出 Excel
          </Button>
        </Space>
      }
    >
      {/* 篩選 */}
      <div style={{ marginBottom: space[4] }}>
        <SectionCard title="篩選條件">
          <RangePicker
            value={dateRange}
            onChange={(v) => setDateRange(v)}
            placeholder={['開始日期', '結束日期']}
            format="YYYY-MM-DD"
          />
        </SectionCard>
      </div>

      {/* 橫向柱狀圖 */}
      <div style={{ marginBottom: space[4] }}>
        <SectionCard title="稽核員案件數排行">
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: space[8] }}>
              <Spin />
            </div>
          ) : auditors.length === 0 ? (
            <EmptyState title="暫無稽核員資料" size="sm" />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, auditors.length * 36)}>
              <BarChart
                layout="vertical"
                data={auditors}
                margin={{ top: 8, right: 40, bottom: 8, left: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80} />
                <RechartTooltip formatter={(v) => [`${String(v)} 件`, '案件數']} />
                <Bar dataKey="caseCount" name="案件數" radius={[0, 3, 3, 0]}>
                  {auditors.map((_, idx) => (
                    <Cell
                      key={idx}
                      fill={
                        idx === 0 ? color.brand.primary : `rgba(79, 70, 229, ${0.85 - idx * 0.1})`
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
      </div>

      {/* 表格詳情 */}
      <SectionCard title={`稽核員統計明細（${auditors.length} 位）`}>
        <Table
          dataSource={auditors}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          size="middle"
        />
      </SectionCard>
    </PageShell>
  );
}
